import { audioEndMs, audioLengthMs, audioSourceKey, editDurationMs, pieceSounds, segmentLengthMs, segmentStarts, sourceKey } from './edit.js';
/**
 * What an edit sounds like, as plain data: the pieces' own sound (at their speed, through their transitions' handles),
 * the audio clips with their fades, each track's level after mute and solo, the music's drop under the voice-over
 * and in the dips, the end fade. The editor's monitor and its export schedule exactly this through Web Audio; the
 * playout's render of the edit for air (edit-air.ts) sends the same voices with their levels multiplied out.
 */
const clamp = (x, a, b) => Math.min(b, Math.max(a, x));
const isDip = (t) => t === 'dip-black' || t === 'dip-white';
/** A level between points, flat before the first and after the last. */
export function gainAt(points, tS) {
    if (!points.length)
        return 1;
    if (tS <= points[0].atS)
        return points[0].gain;
    for (let k = 1; k < points.length; k++) {
        const a = points[k - 1];
        const b = points[k];
        if (tS <= b.atS)
            return b.atS === a.atS ? b.gain : a.gain + ((b.gain - a.gain) * (tS - a.atS)) / (b.atS - a.atS);
    }
    return points[points.length - 1].gain;
}
/** Points of a piecewise-linear level, at its corners inside [from, to]. */
function pointsOf(level, corners, fromS, toS) {
    const times = [...new Set([fromS, toS, ...corners.filter((c) => c > fromS && c < toS)])].sort((a, b) => a - b);
    return times.map((atS) => ({ atS, gain: Math.round(level(atS) * 10_000) / 10_000 }));
}
const effective = (t, anySolo) => (t.mute ? 0 : anySolo && !t.solo ? 0 : t.gain);
/** The sections where the music drops, as fractions of its level: under the voice-over (when ducking is on) and the dips. */
export function lowSections(p, voAudible) {
    const out = [];
    if (p.mix.music.duck && voAudible)
        for (const c of p.audio)
            if (c.track === 'vo')
                out.push({ fromS: c.atMs / 1000, toS: audioEndMs(c) / 1000, level: p.mix.music.duckLevel });
    for (const d of p.dips)
        out.push({ fromS: d.fromMs / 1000, toS: d.toMs / 1000, level: d.level });
    return out;
}
/** The music's share of its level at a second: the lowest of the sections covering it. */
export function musicFractionAt(sections, tS) {
    let f = 1;
    for (const s of sections)
        if (tS >= s.fromS && tS < s.toS)
            f = Math.min(f, s.level);
    return f;
}
/**
 * The music's share of its level along the edit, as points between which it ramps: every drop ramps over 0.3 s
 * before the section starts, every rise over 0.6 s after it ends; where sections overlap the lowest wins.
 */
export function musicEnvelope(p, voAudible) {
    const durS = editDurationMs(p) / 1000;
    const sections = lowSections(p, voAudible).filter((s) => s.toS > 0 && s.fromS < durS);
    if (!sections.length)
        return [{ atS: 0, gain: 1 }];
    const edges = [...new Set(sections.flatMap((s) => [s.fromS, s.toS]).filter((x) => x > 0 && x < durS))].sort((a, b) => a - b);
    const pts = [];
    let last = musicFractionAt(sections, 1e-6);
    pts.push({ atS: 0, gain: last });
    for (const t of edges) {
        const next = musicFractionAt(sections, t + 1e-6);
        if (next === last)
            continue;
        if (next < last)
            pts.push({ atS: Math.max(pts[pts.length - 1].atS, t - 0.3), gain: last }, { atS: t, gain: next });
        else
            pts.push({ atS: t, gain: last }, { atS: Math.min(durS, t + 0.6), gain: next });
        last = next;
    }
    return pts;
}
/** Everything the edit sounds like: the pieces' own sound (at their speed) with their crossfades, the clips with their fades, the levels. */
export function mixPlan(p, durations = { video: () => null, audio: () => null }) {
    const totalMs = editDurationMs(p);
    const totalS = totalMs / 1000;
    const starts = segmentStarts(p);
    const voices = [];
    p.segments.forEach((seg, i) => {
        // a frozen frame and a photo are silent; a piece whose sound was separated is heard from its clips on A3
        if (!pieceSounds(seg))
            return;
        const rate = seg.speed;
        const start = starts[i] / 1000;
        const end = start + segmentLengthMs(seg) / 1000;
        const trIn = i > 0 ? seg.transitionIn : null;
        const fadeFromBlack = i === 0 ? seg.transitionIn : null;
        const trOut = p.segments[i + 1]?.transitionIn ?? null;
        const dIn = trIn ? trIn.durationMs / 1000 : 0;
        const dOut = trOut ? trOut.durationMs / 1000 : 0;
        // the sound reaches into the handles as far as the picture does (half the transition), where the file has them
        let soundStart = start - dIn / 2;
        let srcStart = seg.inMs / 1000 - (dIn / 2) * rate;
        if (srcStart < 0) {
            soundStart -= srcStart / rate;
            srcStart = 0;
        }
        let soundEnd = end + dOut / 2;
        const srcEnd = seg.outMs / 1000 + (dOut / 2) * rate;
        const durMs = durations.video(seg.source);
        if (durMs !== null && srcEnd > durMs / 1000)
            soundEnd -= (srcEnd - durMs / 1000) / rate;
        if (soundEnd - soundStart <= 0.001)
            return;
        const inFactor = (t) => {
            if (fadeFromBlack)
                return clamp(t / (fadeFromBlack.durationMs / 1000), 0, 1);
            if (!trIn)
                return 1;
            return isDip(trIn.type) ? clamp((t - start) / (dIn / 2), 0, 1) : clamp((t - (start - dIn / 2)) / dIn, 0, 1);
        };
        const outFactor = (t) => {
            if (!trOut)
                return 1;
            return isDip(trOut.type) ? clamp((end - t) / (dOut / 2), 0, 1) : clamp((end + dOut / 2 - t) / dOut, 0, 1);
        };
        const corners = [start - dIn / 2, start, start + dIn / 2, end - dOut / 2, end, end + dOut / 2, ...(fadeFromBlack ? [fadeFromBlack.durationMs / 1000] : [])];
        voices.push({
            key: sourceKey(seg.source),
            kind: 'piece',
            track: 'video',
            atS: soundStart,
            offsetS: srcStart,
            durS: soundEnd - soundStart,
            rate,
            points: pointsOf((t) => seg.gain * inFactor(t) * outFactor(t), corners, soundStart, soundEnd),
        });
    });
    for (const c of p.audio) {
        const at = c.atMs / 1000;
        const len = audioLengthMs(c) / 1000;
        const end = Math.min(at + len, totalS);
        if (end - at <= 0.001)
            continue;
        const fi = c.fadeInMs / 1000;
        const fo = c.fadeOutMs / 1000;
        const clipEnd = at + len;
        const level = (t) => c.gain * (fi > 0 ? clamp((t - at) / fi, 0, 1) : 1) * (fo > 0 ? clamp((clipEnd - t) / fo, 0, 1) : 1);
        voices.push({ key: audioSourceKey(c.source), kind: 'audio', track: c.track, atS: at, offsetS: c.inMs / 1000, durS: end - at, rate: 1, points: pointsOf(level, [at + fi, clipEnd - fo], at, end) });
    }
    const sound = p.mix.sound ?? { gain: 1, mute: false, solo: false };
    const anySolo = p.mix.video.solo || p.mix.vo.solo || p.mix.music.solo || sound.solo;
    const tracks = { video: effective(p.mix.video, anySolo), vo: effective(p.mix.vo, anySolo), music: effective(p.mix.music, anySolo), sound: effective(sound, anySolo) };
    const master = p.endFade && totalS > 0 ? [{ atS: Math.max(0, totalS - p.endFade.durationMs / 1000), gain: 1 }, { atS: totalS, gain: 0 }] : [{ atS: 0, gain: 1 }];
    return { totalS, voices, tracks, musicEnvelope: musicEnvelope(p, tracks.vo > 0), master };
}
//# sourceMappingURL=edit-mix.js.map